import React from 'react';
import {MenuCommand, ThreeDotsMenu} from 'front-onceforall-core';
import {I18n, Translate} from 'react-redux-i18n';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {COUNTRY} from 'front-onceforall-core/dist/utils/country';
import {interventionPropTypes} from '../../../utils/propTypes';
import authenticationService from '../../../services/authentication/AuthenticationService';
import {interventionRole, MANAGE_PROJECT_PERMISSION, PROJECT_STATUS, projectType, status} from '../../../utils/constant';
import documentService from '../../../services/document/DocumentService';
import {hasFiles, hasValidOrNotApplicableFiles} from '../../../utils/document';
import {openOrSaveFile} from '../../../utils/http';
import ConfirmModal from '../ConfirmModal';
import ProjectTreeRowDocuments from './ProjectTreeRowDocuments';
import InterventionDateModal from '../../interventionDateModal';

export function ProjectTreeRowActionsMenu(props) {
  const [hasDownloadableFiles, setHasDownloadableFiles] = React.useState(null);
  const [confirmEndOrganisationModalOpen, setConfirmEndOrganisationModalOpen] = React.useState(false);
  const [interventionDateModalOpen, setInterventionDateModalOpen] = React.useState(false);
  const [showDocuments, setShowDocuments] = React.useState(false);

  const {intervention} = props;
  const isBackOffice = authenticationService.roles().isBackOffice();
  const canManageProject = authenticationService.roles().hasPermission(MANAGE_PROJECT_PERMISSION);
  const iAmProjectOwner = props.myIntervention.project.ownerOrganisation.organisationId === props.organisation.organisationId;
  const isSelf = intervention.interventionId === props.myIntervention.interventionId;
  const isProjectClient = intervention.role === interventionRole.CLIENT;
  const isProjectManagerOrGeneralContractor = [interventionRole.GENERAL_CONTRACTOR, interventionRole.PROJECT_MANAGER, interventionRole.PROJECT_MANAGER_CLIENT].includes(intervention.role);
  const isProjectActive = props.myIntervention.project.projectStatus === PROJECT_STATUS.ACTIVE;
  const isProjectFrench = props.myIntervention.project.country === COUNTRY.FRANCE;
  const isFamilyProject = props.myIntervention.project.projectType === projectType.FAMILY;
  const isEnded = intervention.status === status.ENDED;

  const displayEndOrganisationButton = iAmProjectOwner && canManageProject && !isSelf && !isProjectClient && isProjectActive && !isEnded;
  const displaySetInterventionDate = isBackOffice && isProjectActive && isProjectFrench && !isFamilyProject && isProjectManagerOrGeneralContractor;

  const handleMenuBtnClick = () => {
    if (isSelf) {
      documentService.searchByProject(intervention.interventionId)
        .then(response => setHasDownloadableFiles(hasFiles(response)));
    } else {
      const subcontractorId = intervention.organisation.organisationId;
      documentService.searchByProjectOfSubcontractor(intervention.interventionId, subcontractorId, props.organisation.organisationId)
        .then(response => setHasDownloadableFiles(hasValidOrNotApplicableFiles(response)));
    }
  };

  const handleDownloadDossierProject = () => {
    if (!hasDownloadableFiles) {
      return;
    }

    const url = isSelf ? documentService.getProjectDossierZipUrl(intervention.interventionId, props.currentLocale) :
      documentService.getProjectDossierOfSubcontractorZipUrl(intervention.interventionId, intervention.organisation.organisationId, props.organisation.organisationId, props.currentLocale);

    authenticationService.authRedirect(url)
      .then(authUrl => openOrSaveFile(authUrl));
  };

  const handlePrintDossierProject = () => {
    if (!hasDownloadableFiles) {
      return;
    }

    const url = isSelf ?
      documentService.getProjectDossierPdfUrl(intervention.interventionId, props.currentLocale) :
      documentService.getProjectDossierOfSubcontractorPdfUrl(intervention.interventionId, intervention.organisation.organisationId, props.organisation.organisationId, props.currentLocale);

    authenticationService.authRedirect(url)
      .then(authUrl => openOrSaveFile(authUrl));
  };

  function handleEndOrganisation() {
    props.onSubmitEndOrganisation(intervention);
    setConfirmEndOrganisationModalOpen(false);
  }

  const handleDownloadDossierOverview = () => authenticationService.authRedirect(
    documentService.downloadDossierOverviewProject(intervention.interventionId, props.currentLocale)
  )
    .then(authUrl => openOrSaveFile(authUrl));

  const handleShowDocuments = () => {
    setShowDocuments(true);
  };

  function handleCloseInterventionDateModal(refresh = false) {
    setInterventionDateModalOpen(false);

    if (refresh) {
      props.onRefreshProjectData();
    }
  }

  return (
    <>
      <ThreeDotsMenu
        data-id="intervention-actions"
        onOpen={handleMenuBtnClick}
      >
        <MenuCommand
          disabled={isSelf && !props.organisation.depositor}
          onClick={handleShowDocuments}
          data-id="menu-show-documents"
          label={<Translate value="project.buttonDocuments.showDocuments"/>}
          icon="document-page"
          disabledMessage={I18n.t('project.buttonDocuments.expand.tooltip')}
        />
        <MenuCommand
          disabled={!hasDownloadableFiles}
          onClick={handleDownloadDossierProject}
          data-id="menu-download-dossier-project"
          label={<Translate value="project.buttonDocuments.download"/>}
          disabledMessage={I18n.t('organisation.depositorService.noFile')}
          icon={hasDownloadableFiles === null ? 'loader' : 'folder-download'}
        />
        <MenuCommand
          disabled={!hasDownloadableFiles}
          onClick={handlePrintDossierProject}
          data-id="menu-print-dossier-project"
          label={<Translate value="project.buttonDocuments.print"/>}
          disabledMessage={I18n.t('organisation.depositorService.noFile')}
          icon={hasDownloadableFiles === null ? 'loader' : 'print'}
        />
        <MenuCommand
          disabled={!intervention.organisation.depositor}
          onClick={handleDownloadDossierOverview}
          data-id="menu-download-dossier-overview"
          label={<Translate value="dossier.download.dossierOverview"/>}
          disabledMessage={I18n.t('organisation.depositorService.notActivated')}
          icon={hasDownloadableFiles === null ? 'loader' : 'download'}
        />
        {displayEndOrganisationButton &&
          <MenuCommand
            onClick={() => setConfirmEndOrganisationModalOpen(true)}
            data-id="menu-end-organisation"
            label={<Translate value="intervention.ended.buttonTitle"/>}
            icon="unlink"
          />
        }
        {displaySetInterventionDate &&
          <MenuCommand
            onClick={() => setInterventionDateModalOpen(true)}
            data-id="menu-set-intervention-date"
            label={<Translate value="intervention.dateForm.openButton"/>}
            icon="calendar"
            BOCommand
          />
        }
      </ThreeDotsMenu>

      <ConfirmModal
        isOpen={confirmEndOrganisationModalOpen}
        onClose={() => setConfirmEndOrganisationModalOpen(false)}
        onConfirm={handleEndOrganisation}
        content={<Translate
          value="intervention.ended.confirmEndOrganisation.content"
          organisationName={intervention.organisation.fullName}
        />}
        warning={<Translate value="intervention.ended.confirmEndOrganisation.warning"/>}
      />

      {showDocuments &&
        <ProjectTreeRowDocuments
          data-id="project-tree-row-documents"
          myIntervention={props.myIntervention}
          intervention={props.intervention}
          onCloseRequest={() => setShowDocuments(false)}
        />
      }

      <InterventionDateModal
        intervention={props.intervention}
        onClose={handleCloseInterventionDateModal}
        isOpen={interventionDateModalOpen}
      />
    </>
  );
}

ProjectTreeRowActionsMenu.propTypes = {
  intervention: interventionPropTypes().isRequired,
  myIntervention: interventionPropTypes().isRequired,
  organisation: PropTypes.object.isRequired,
  currentLocale: PropTypes.string.isRequired,
  onSubmitEndOrganisation: PropTypes.func.isRequired,
  onRefreshProjectData: PropTypes.func.isRequired
};

function mapStateToProps(state) {
  return {
    organisation: state.organisation,
    currentLocale: state.i18n.locale
  };
}

export default connect(mapStateToProps)(ProjectTreeRowActionsMenu);
